import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getDeviceId } from "@/lib/device";
import { setSentryUser, addBreadcrumb } from "@/lib/sentry";

interface AuthCtx {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up listener FIRST
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setLoading(false);

      if (s?.user) {
        setSentryUser(s.user);
        addBreadcrumb({
          category: "auth",
          message: `Auth state changed: ${event}`,
          level: "info",
        });
        // Register device fingerprint on sign-in (defer to avoid deadlock with auth callback)
        setTimeout(() => {
          supabase.rpc("register_device", {
            p_device_id: getDeviceId(),
            p_label: navigator.userAgent.slice(0, 80),
          });
        }, 0);
      } else {
        setSentryUser(null);
      }
    });

    // Then check existing session
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
      setSentryUser(data.session?.user ?? null);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSentryUser(null);
  };

  return (
    <Ctx.Provider value={{ session, user: session?.user ?? null, loading, signOut }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);
